import { useDroppable } from '@dnd-kit/core';
import AddIcon from '@mui/icons-material/Add';
import { Box, IconButton, styled } from '@mui/material';
import { useState } from 'react';
import { useDebounce } from 'react-use';

import { ELayoutManagerMode, type EWidgetSection } from '#shared';

import { useSelectWidgetManagerDraggingWidget } from '../../widgetManager';
import { useLayoutMode } from '../model';

const PREFIX = 'TSMED-BlockAddButton';

export const blockAddButtonClasses = {
  root: `${PREFIX}-root`,
  button: `${PREFIX}-button`,
  dragTrigger: `${PREFIX}-dragTrigger`,
} as const;

const Root = styled(Box, {
  name: PREFIX,
  slot: 'Root',
  shouldForwardProp: prop =>
    prop !== '$isOver' &&
    prop !== '$isActive' &&
    prop !== '$isEmpty' &&
    prop !== '$isAwaitingPlacement',
})<{
  $isOver?: boolean;
  $isActive?: boolean;
  $isEmpty?: boolean;
  $isAwaitingPlacement?: boolean;
}>(({
  theme: { palette, utils, shadows },
  $isOver,
  $isActive,
  $isEmpty,
  $isAwaitingPlacement,
}) => {
  // Active/expanded state is triggered by isOver, isActive (clicked), or hover
  const isExpanded = $isOver || $isActive;

  // Highlight state when palette widget is selected (awaiting placement)
  const isHighlighted = $isAwaitingPlacement || $isOver || $isActive;

  // For empty sections, always show as full-height expanded container
  if ($isEmpty) {
    return {
      [`&.${blockAddButtonClasses.root}`]: {
        position: 'relative',
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        cursor: 'pointer',
        transition: 'all 0.15s ease-in-out',
        zIndex: 10,
        flex: 1,
        minHeight: '100px',
        backgroundColor: isHighlighted
          ? `${palette.mapBlue?.[300] ?? palette.primary.main}30`
          : `${palette.mapBlue?.[800] ?? palette.primary.main}40`,
        borderRadius: '8px',
        border: isHighlighted
          ? `2px dashed ${palette.mapBlue?.[300] ?? palette.primary.main}`
          : `2px dashed ${palette.mapBlue?.[500] ?? palette.primary.main}60`,
        boxShadow: isHighlighted
          ? `0 0 20px ${palette.mapBlue?.[300] ?? palette.primary.main}50`
          : 'none',
        // Pulsing animation when awaiting placement
        animation: $isAwaitingPlacement
          ? 'pulse-awaiting 2s ease-in-out infinite'
          : 'none',

        '&:hover': {
          backgroundColor: `${palette.mapBlue?.[300] ?? palette.primary.main}30`,
          border: `2px dashed ${palette.mapBlue?.[300] ?? palette.primary.main}`,
        },

        // Glowing overlay effect when highlighted
        '&::before': isHighlighted
          ? {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              borderRadius: '8px',
              boxShadow:
                'inset 0 0 25px rgba(33, 150, 243, 0.5), inset 0 4px 18px rgba(33, 150, 243, 0.4)',
              pointerEvents: 'none',
              zIndex: 50,
            }
          : {},

        // Tooltip showing action hint
        '&::after': $isAwaitingPlacement
          ? {
              content: '"Click to add widget here"',
              position: 'absolute',
              top: '-28px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'rgba(33, 150, 243, 0.95)',
              color: '#fff',
              padding: '4px 12px',
              borderRadius: '12px',
              fontSize: '11px',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              boxShadow: '0 2px 8px rgba(33, 150, 243, 0.6)',
              zIndex: 100,
            }
          : $isOver
            ? {
                content: '"Drop widget here"',
                position: 'absolute',
                top: '-28px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'rgba(33, 150, 243, 0.95)',
                color: '#fff',
                padding: '4px 12px',
                borderRadius: '12px',
                fontSize: '11px',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                boxShadow: '0 2px 8px rgba(33, 150, 243, 0.6)',
                zIndex: 100,
              }
            : {},
      },

      [`& .${blockAddButtonClasses.dragTrigger}`]: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'transparent',
        pointerEvents: 'auto',
        zIndex: 1,
        cursor: 'pointer',
      },

      [`& .${blockAddButtonClasses.button}`]: {
        backgroundColor: palette.mapBlue?.[300] ?? palette.primary.main,
        color: palette.getContrastText(
          palette.mapBlue?.[300] ?? palette.primary.main,
        ),
        width: '48px',
        height: '48px',
        padding: 0,
        zIndex: 2,
        boxShadow: shadows[2],
        transition: 'transform 0.15s ease-in-out',

        '&:hover': {
          backgroundColor: palette.mapBlue?.[400] ?? palette.primary.dark,
          transform: 'scale(1.1)',
        },

        '& svg': {
          fontSize: '32px',
        },
      },

      // Text for empty section
      [`& .${blockAddButtonClasses.root}-text`]: {
        color: palette.common.white,
        fontSize: '14px',
        opacity: 0.7,
        fontStyle: 'italic',
        textAlign: 'center',
        zIndex: 2,
      },

      // Pulsing animation keyframes
      '@keyframes pulse-awaiting': {
        '0%, 100%': {
          boxShadow: `0 0 20px ${palette.mapBlue?.[300] ?? palette.primary.main}50`,
        },
        '50%': {
          boxShadow: `0 0 35px ${palette.mapBlue?.[300] ?? palette.primary.main}80`,
        },
      },
    };
  }

  return {
    [`&.${blockAddButtonClasses.root}`]: {
      position: 'relative',
      // Collapse to 0 height to not steal space, but add padding for drag zone
      height: isExpanded ? utils.clamp(100, 200) : '0',
      // Add padding-top to create invisible drag zone above without affecting layout
      paddingTop: isExpanded ? '0' : '30px',
      marginTop: isExpanded ? '0' : '-30px',
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      transition: $isActive
        ? 'height 0.1s ease-in-out, padding-top 0.1s ease-in-out, margin-top 0.1s ease-in-out'
        : 'all 0.1s ease-in-out',
      zIndex: 10,
      flexShrink: 0,
      backgroundColor: isExpanded
        ? `${palette.mapBlue?.[300] ?? palette.primary.main}20`
        : 'transparent',
      borderRadius: isExpanded ? '8px' : '0',
      border: isExpanded
        ? `2px dashed ${palette.mapBlue?.[300] ?? palette.primary.main}`
        : 'none',
      boxShadow: $isActive
        ? `0 0 20px ${palette.mapBlue?.[300] ?? palette.primary.main}50`
        : 'none',

      '&:hover': {
        height: utils.clamp(100, 200),
        paddingTop: '0',
        marginTop: '0',
        backgroundColor: `${palette.mapBlue?.[300] ?? palette.primary.main}20`,
        borderRadius: '8px',
        border: `2px dashed ${palette.mapBlue?.[300] ?? palette.primary.main}`,
      },

      // Show button with low opacity by default
      [`& .${blockAddButtonClasses.button}`]: {
        transform: isExpanded ? 'scale(1.2)' : 'scale(0.8)',
      },

      [`&:hover .${blockAddButtonClasses.button}`]: {
        opacity: 1,
        transform: 'scale(1.2)',

        '& svg': {
          color: 'white',
        },
      },
    },

    [`& .${blockAddButtonClasses.dragTrigger}`]: {
      position: 'absolute',
      // Position to cover the padding area above and the content area
      top: isExpanded ? '0' : '-30px',
      left: 0,
      right: 0,
      // Cover 60px total (30px padding + 30px below when collapsed)
      height: isExpanded ? '100%' : '60px',
      backgroundColor: 'transparent',
      pointerEvents: 'auto',
      zIndex: 1,
      cursor: 'pointer',
    },

    [`& .${blockAddButtonClasses.button}`]: {
      position: 'absolute',
      backgroundColor: palette.mapBlue?.[300] ?? palette.primary.main,
      color: palette.getContrastText(
        palette.mapBlue?.[300] ?? palette.primary.main,
      ),
      width: '38px',
      height: '38px',
      padding: 0,
      zIndex: 2,
      boxShadow: shadows[2],

      '&:hover': {
        backgroundColor: palette.mapBlue?.[400] ?? palette.primary.dark,
        transform: 'scale(1.1)',
      },

      '& svg': {
        fontSize: '38px',
      },
    },
  };
});

export interface BlockAddButtonProps {
  sectionId: EWidgetSection;
  position: number;
  onClick: (position: number) => void;
  disabled?: boolean;
  /**
   * When true, renders as a full-size placeholder for empty sections.
   * This makes the button fill the entire section area with "Drop widget here" text.
   */
  isEmpty?: boolean;
}

/**
 * Component that shows a (+) button between blocks to add new blocks/widgets.
 * Only visible in EDIT mode. Also acts as a drop target for widgets.
 */
export const BlockAddButton = ({
  sectionId,
  position,
  onClick,
  disabled = false,
  isEmpty = false,
}: BlockAddButtonProps) => {
  const { mode, pendingPlacement, setPendingPlacement, selectedPaletteWidget } =
    useLayoutMode();
  const draggingWidget = useSelectWidgetManagerDraggingWidget();

  // Check if this button is actively waiting for widget placement
  const isActive =
    pendingPlacement !== null &&
    pendingPlacement.sectionId === sectionId &&
    pendingPlacement.position === position;

  // Check if a palette widget is selected (awaiting placement)
  // Only highlight empty sections when a palette widget is selected
  const isAwaitingPlacement =
    isEmpty &&
    mode === ELayoutManagerMode.EDIT &&
    selectedPaletteWidget !== null;

  // Check if we're dragging a widget from the same section (reordering)
  // In this case, we don't want to expand the add button
  const isDraggingFromSameSection =
    draggingWidget?.type === 'widget-instance' &&
    draggingWidget.widget?.sectionId === sectionId;

  // Make this a droppable target with unique ID
  const droppableId = `${sectionId}__add__${position}`;
  const { setNodeRef, isOver } = useDroppable({
    id: droppableId,
    disabled: disabled || mode !== ELayoutManagerMode.EDIT,
    data: {
      type: 'add-button',
      sectionId,
      position,
    },
  });

  // Debounce isOver state to prevent flickering
  // But suppress isOver when dragging from same section (just reordering)
  const effectiveIsOver = isOver && !isDraggingFromSameSection;
  const [debouncedIsOver, setDebouncedIsOver] = useState(false);
  useDebounce(() => setDebouncedIsOver(effectiveIsOver), 50, [effectiveIsOver]);

  // Only show in EDIT mode
  if (mode !== ELayoutManagerMode.EDIT) {
    return null;
  }

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) {
      return;
    }
    // If already active, clear pending placement (toggle off)
    if (isActive) {
      setPendingPlacement(null);
      return;
    }

    onClick(position);
  };

  return (
    <Root
      ref={setNodeRef}
      className={blockAddButtonClasses.root}
      onClick={handleClick}
      $isOver={debouncedIsOver}
      $isActive={isActive}
      $isEmpty={isEmpty}
      $isAwaitingPlacement={isAwaitingPlacement}
      sx={{
        opacity: disabled ? 0.3 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      <Box className={blockAddButtonClasses.dragTrigger} />
      <IconButton
        className={blockAddButtonClasses.button}
        disabled={disabled}
        onClick={handleClick}
      >
        <AddIcon />
      </IconButton>
      {isEmpty && (
        <Box className={`${blockAddButtonClasses.root}-text`}>
          Drop widget here or click to add
        </Box>
      )}
    </Root>
  );
};
